import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import type { QueryClient } from "@tanstack/react-query";

import { queryKeys, SHIPPING_OPTIONS_SEGMENT } from "./queryKeys";

type StoreSellerCartsResponse = SalonStockerHttpTypes.StoreSellerCartsResponse;
type StoreSellerCartShippingMethod =
  SalonStockerHttpTypes.StoreSellerCart["shipping_methods"][number];
type StoreSellerCartLineItem =
  SalonStockerHttpTypes.StoreSellerCart["items"][number];

/**
 * Invalidate every seller-cart query for the authenticated customer - the one
 * place that owns the seller-cart cache key.
 *
 * Any action that lands items in (or otherwise mutates) a seller cart calls
 * this instead of naming `sellerCarts.all` inline: the cart mutations
 * themselves (add / update / remove / shipping / promo / address / complete /
 * external-checkout), plus the cross-module actions that fan into a cart -
 * adding a saved list, and confirming an order or stock reorder. Centralising
 * it keeps those call sites from re-naming the key and drifting.
 *
 * Initiating a payment session used to be on that list and deliberately is not
 * any more. It writes a payment collection onto the cart, but nothing the
 * checkout page renders reads it, so the two `staleTime: 0` refetches per cart
 * bought nothing - and the new `carts` array identity they produced re-rendered
 * the payment step mid-initialisation. The rule the list encodes is "changed
 * something a cart view shows", not "touched a cart".
 *
 * It stays deliberately broad across *carts* - invalidates `sellerCarts.all`,
 * not a single `sellerCarts.detail(id)` - because those callers routinely
 * touch several carts at once (a saved list or reorder fans across suppliers)
 * and the cart set is small.
 *
 * It deliberately does NOT sweep in shipping options, even though they sit
 * under the same key prefix and React Query matches prefixes. Those queries
 * are per-cart and neither query sets a `staleTime`, so including them meant
 * one quantity click on a four-supplier cart fired five refetches - four of
 * them for option lists that a quantity change on *another* cart cannot
 * affect. The carts whose contents actually changed refresh their own options
 * via {@link invalidateCartShippingOptions}, which is exact and costs one
 * request instead of N.
 */
export function invalidateSellerCarts(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.sellerCarts.all,
    predicate: (query) => query.queryKey[1] !== SHIPPING_OPTIONS_SEGMENT
  });
}

/**
 * Invalidate one cart's shipping options - the exact key, nothing else.
 *
 * Called by the mutations that can genuinely change what a cart is quoted:
 * anything that moves its subtotal across a free-shipping threshold (add /
 * update / remove / switch supplier / promo) and anything that changes where
 * it ships to (address updates). Picking a shipping *method* is not one of
 * them: that changes which option is selected, not which options exist or
 * what they cost.
 *
 * A blank `cartId` is a no-op so callers can pass an optional id straight
 * through without guarding.
 */
export function invalidateCartShippingOptions(
  queryClient: QueryClient,
  cartId: string | null | undefined
) {
  if (!cartId) return Promise.resolve();
  return queryClient.invalidateQueries({
    queryKey: queryKeys.sellerCarts.shippingOptions(cartId),
    exact: true
  });
}

// ============================================================
// Optimistic mutation cache
// ============================================================
//
// `cache.ts` owns the seller-cart cache for the optimistic mutation path too,
// not just invalidation: the snapshot/rollback pair and the pure patch
// functions the cart mutations apply. The optimistic *write* (`setQueryData`)
// stays visible at each mutation - only the reusable scaffold lives here.
// Mirrors the shape `queries/customer/stock-tracking.ts` already uses
// (`snapshotLists` / `rollbackLists` + `applyUpsertToList`).

/** Snapshot of the seller-carts cache captured before an optimistic update. */
export interface SellerCartsMutationContext {
  previousCarts: StoreSellerCartsResponse | undefined;
}

/**
 * Cancel any in-flight seller-cart refetch and snapshot the current cache, so a
 * failed optimistic update can be rolled back. Returns the mutation context each
 * cart mutation's `onMutate` returns and its `onError` reads.
 */
export async function snapshotSellerCarts(
  queryClient: QueryClient
): Promise<SellerCartsMutationContext> {
  await queryClient.cancelQueries({ queryKey: queryKeys.sellerCarts.all });
  const previousCarts = queryClient.getQueryData<StoreSellerCartsResponse>(
    queryKeys.sellerCarts.all
  );
  return { previousCarts };
}

/** Restore the pre-mutation seller-carts cache after a failed optimistic update. */
export function rollbackSellerCarts(
  queryClient: QueryClient,
  context?: SellerCartsMutationContext
) {
  if (context?.previousCarts) {
    queryClient.setQueryData(queryKeys.sellerCarts.all, context.previousCarts);
  }
}

/**
 * Rescale a line item's money to a new quantity.
 *
 * Scaled from the item's existing figures rather than recomputed as
 * `unit_price * quantity`, so whatever per-item tax or discount ratio the
 * server applied survives the patch. Falls back to the unit price when there
 * is no previous quantity to scale from.
 */
function rescaleLineItem(
  item: StoreSellerCartLineItem,
  quantity: number
): StoreSellerCartLineItem {
  const base = item.unit_price * quantity;
  if (item.quantity <= 0) {
    return { ...item, quantity, subtotal: base, total: base };
  }

  const factor = quantity / item.quantity;
  return {
    ...item,
    quantity,
    // `?? 0` matches `setLineItemQuantity`'s own reduce below: an absent
    // total/subtotal reads as "not yet priced", same as everywhere else in
    // this optimistic-update path.
    subtotal: (item.subtotal ?? 0) * factor,
    total: (item.total ?? 0) * factor
  };
}

/**
 * Optimistically set a line item's quantity in one cart. Pure - returns the next
 * cache value, touching only the matching cart and leaving every other cart and
 * response field untouched. A cart or item that isn't found leaves the response
 * unchanged.
 *
 * The item's own money and the cart's `item_subtotal`/`total` move with it, so
 * the stepper, the line's price and the summary rail all update on the click
 * rather than waiting for the server. Tax and discount totals are left for the
 * server to restate - there is no per-item allocation to re-derive them from,
 * and they are a second-order correction on a figure that is about to be
 * replaced by the authoritative one anyway.
 */
export function setLineItemQuantity(
  carts: StoreSellerCartsResponse,
  cartId: string,
  lineItemId: string,
  quantity: number
): StoreSellerCartsResponse {
  return {
    ...carts,
    carts: carts.carts.map((cart) => {
      if (cart.cart_id !== cartId) return cart;

      const items = cart.items.map((item) =>
        item.id === lineItemId ? rescaleLineItem(item, quantity) : item
      );
      const itemSubtotal = items.reduce(
        (sum, item) => sum + (item.subtotal ?? 0),
        0
      );

      return {
        ...cart,
        items,
        totals: {
          ...cart.totals,
          item_subtotal: itemSubtotal,
          total:
            itemSubtotal +
            cart.totals.shipping_total +
            cart.totals.tax_total -
            cart.totals.discount_total
        }
      };
    })
  };
}

/**
 * Optimistically set one cart's shipping method. Pure - returns the next cache
 * value with that cart's `shipping_methods` replaced by the chosen option.
 *
 * The selector reads its value straight from cart data, so without this the
 * dropdown kept showing the old method until BOTH the POST and the follow-up
 * cart refetch had landed - two serial round trips before a click registered.
 * Every other cart mutation already patched first; this one didn't.
 *
 * `id` is a placeholder: the real shipping-method id is server-generated and
 * nothing in the app reads it (consumers read `shipping_option_id`, `name`
 * and `length`). The refetch replaces the whole record moments later.
 *
 * `amount` is the option's list price, which is what the buyer picked from.
 * The cart's own `totals` are deliberately left alone - promotions and
 * threshold reprices apply to `shipping_total`, so asserting it here would
 * risk showing a figure the server then contradicts.
 *
 * `provenance` must be restated rather than carried over, because this record
 * is rebuilt rather than spread and the field is load-bearing (KAN-105): its
 * absence means "this seller is not a direct integration", so dropping it
 * would assert that outright, and copying the old value would leave a green
 * "Live" over a list price nothing has live-quoted - the stale green AC8
 * exists to prevent. `"estimated"` is what the optimistic amount actually is:
 * SalonStocker's own list price, straight off the shipping-options endpoint,
 * with no supplier round trip behind it. The refetch flips it to `"live"` if
 * the server does quote it. Erring pessimistic is the safe direction; erring
 * green is the one that misleads.
 *
 * Gated on `cart.totals.provenance`, which the API sets if and only if the
 * seller is a live integration - so a non-integration cart still gets no
 * marker at all, and no grey chip appears where AC5 wants none.
 *
 * `totals.provenance` is downgraded alongside it. Readers are split: the cart
 * page's filtered view recomputes the total's marker from the line items and
 * methods (so it would see the downgrade anyway), while the unfiltered view
 * and the checkout action bar read this field straight. Patching only the
 * method would leave those two green over a total that now contains an
 * unverified freight amount. The money in `totals` is still left alone - that
 * genuinely is the server's to restate, since promotions and threshold
 * reprices apply to `shipping_total`.
 */
export function setCartShippingMethod(
  carts: StoreSellerCartsResponse,
  cartId: string,
  method: Omit<StoreSellerCartShippingMethod, "id">
): StoreSellerCartsResponse {
  return {
    ...carts,
    carts: carts.carts.map((cart) =>
      cart.cart_id === cartId
        ? {
            ...cart,
            shipping_methods: [
              {
                id: `optimistic-${method.shipping_option_id}`,
                ...method,
                ...(cart.totals.provenance
                  ? { provenance: "estimated" as const }
                  : {})
              }
            ],
            totals: cart.totals.provenance
              ? { ...cart.totals, provenance: "estimated" as const }
              : cart.totals
          }
        : cart
    )
  };
}

/**
 * Optimistically remove a line item from one cart. Pure - returns the next cache
 * value with the item gone and, the load-bearing rule, the cart itself dropped
 * once its last item is removed (an empty seller cart is never shown). Other
 * carts and response fields are untouched.
 */
export function removeLineItem(
  carts: StoreSellerCartsResponse,
  cartId: string,
  lineItemId: string
): StoreSellerCartsResponse {
  return {
    ...carts,
    carts: carts.carts
      .map((cart) =>
        cart.cart_id === cartId
          ? {
              ...cart,
              items: cart.items.filter((item) => item.id !== lineItemId)
            }
          : cart
      )
      // Remove carts that become empty.
      .filter((cart) => cart.items.length > 0)
  };
}
